import React, { useState, useEffect, useCallback } from 'react';
import { Calendar as CalendarIcon, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import * as crmBackend from '../services/crmBackendService';
import { useToast } from '../hooks/use-toast';
import { toDateKey } from '../lib/adminAppUtils';
import TechnicianMultiSelect from '../components/TechnicianMultiSelect';

const CalendarPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const { toast } = useToast();
  const userRole = crmBackend.getStoredRole();
  const isTech = userRole === 'tech';
  const techUser = JSON.parse(localStorage.getItem('techUser') || '{}');

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    const now = new Date();
    const startDate = toDateKey(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    const endDate = toDateKey(new Date(now.getFullYear(), now.getMonth() + 3, 0));
    try {
      const result = await crmBackend.getScheduledQuotations(startDate, endDate, isTech ? (techUser.email || null) : null);
      setAppointments((result.data || []).map(q => ({
        id: q.name,
        title: q.party_name,
        date: q.custom_scheduled_date,
        time: q.custom_scheduled_time || '',
        location: q.custom_service_address || '',
        technician: q.custom_technician || '',
        status: q.custom_appointment_status || 'Pending Confirmation',
        jobStatus: crmBackend.normalizeJobStatus(q.custom_job_status),
        amount: q.grand_total,
      })));
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load calendar data', err);
      toast({ title: 'Could not load calendar', description: err.message, variant: 'destructive' });
    }
  }, [isTech, techUser.email, toast]);

  useEffect(() => { loadData(); const id = window.setInterval(loadData, 60000); return () => window.clearInterval(id); }, [loadData]);

  // Days that have appointments (for the calendar dots)
  const appointmentDates = appointments
    .filter(a => a.date)
    .map(a => new Date(a.date + 'T12:00:00'));

  const filteredAppointments = appointments.filter(
    apt => apt.date === toDateKey(selectedDate)
  );

  const allAppointments = [...appointments].sort((a, b) =>
    (a.date + a.time).localeCompare(b.date + b.time)
  );

  const openEdit = async (apt) => {
    try {
      const result = await crmBackend.getEstimateDetail(apt.id);
      const doc = result.data || apt;
      setEditDoc(doc);
      setEditForm({
        custom_appointment_status: doc.custom_appointment_status || '',
        custom_scheduled_date: doc.custom_scheduled_date || apt.date || '',
        custom_scheduled_time: doc.custom_scheduled_time || apt.time || '',
        custom_technician: doc.custom_technician || apt.technician || '',
      });
      setEditOpen(true);
    } catch (err) {
      toast({ title: 'Could not load appointment', description: err.message, variant: 'destructive' });
    }
  };

  const saveEdit = async () => {
    if (!editDoc) return;
    setSaving(true);
    try {
      await crmBackend.updateEstimate(editDoc.name, editForm);
      toast({ title: 'Appointment updated' });
      setEditOpen(false);
      await loadData();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const syncToGCal = async (apt) => {
    try {
      const result = await crmBackend.syncQuotationToGCal(apt.id);
      if (result?.message?.success === false) {
        toast({ title: 'GCal sync failed', description: result.message?.message || 'Unknown error', variant: 'destructive' });
      } else {
        toast({ title: 'Synced to Google Calendar', description: apt.title });
      }
    } catch (err) {
      toast({ title: 'GCal sync error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const sendConfirmation = async (apt) => {
    try {
      const result = await crmBackend.sendAppointmentConfirmation(apt.id);
      if (result?.message?.success === false) {
        toast({ title: 'Email failed', description: result.message?.message, variant: 'destructive' });
      } else {
        toast({ title: 'Confirmation email sent', description: apt.title });
      }
    } catch (err) {
      toast({ title: 'Email error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const syncAll = async () => {
    const scheduled = appointments.filter(a => a.date);
    if (!scheduled.length) {
      toast({ title: 'No scheduled appointments to sync' });
      return;
    }
    setSyncing(true);
    let ok = 0, fail = 0;
    for (const apt of scheduled) {
      try {
        await crmBackend.syncQuotationToGCal(apt.id);
        ok++;
      } catch {
        fail++;
      }
    }
    setSyncing(false);
    toast({ title: `Google Calendar sync complete`, description: `${ok} synced${fail ? `, ${fail} failed` : ''}` });
  };

  const statusColor = (status) => {
    const map = {
      'Pending Confirmation': 'bg-yellow-100 text-yellow-700',
      Confirmed: 'bg-green-100 text-green-700',
      Cancelled: 'bg-red-100 text-red-700',
      'Reschedule Requested': 'bg-purple-100 text-purple-700',
    };
    return map[status] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Calendar</h2>
          <p className="text-gray-500 mt-1">Scheduled appointments from ERPNext</p>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-1">Last refreshed {lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</p>
          )}
        </div>
        <Button onClick={syncAll} disabled={syncing} variant="outline">
          <CalendarIcon className="w-4 h-4 mr-2" />
          {syncing ? 'Syncing…' : 'Sync to Google Calendar'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Date picker with appointment dots */}
        <Card className="lg:col-span-1">
          <CardContent className="p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              modifiers={{ hasAppt: appointmentDates }}
              modifiersClassNames={{ hasAppt: 'font-bold text-blue-600 underline decoration-dotted' }}
              className="rounded-md"
            />
            <p className="text-xs text-gray-400 mt-2 text-center">Underlined dates have appointments</p>
          </CardContent>
        </Card>

        {/* Selected day appointments */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </CardTitle>
            <CardDescription>{filteredAppointments.length} appointment{filteredAppointments.length !== 1 ? 's' : ''}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredAppointments.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No appointments this day</p>
                </div>
              ) : (
                filteredAppointments.map((apt) => (
                  <div key={apt.id} className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => openEdit(apt)}>
                    <div className="p-2 rounded-lg bg-blue-100 text-blue-700 shrink-0">
                      <CalendarIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-gray-900 truncate">{apt.title}</p>
                        <div className="flex gap-1 shrink-0">
                          <Badge className={`text-xs ${statusColor(apt.status)}`} title="Appointment confirmation status">Appt: {apt.status}</Badge>
                          <Badge className={`text-xs ${crmBackend.getJobStatusColor(apt.jobStatus)}`} title="Technician job progress">Job: {apt.jobStatus}</Badge>
                        </div>
                      </div>
                      {apt.time && <p className="text-sm text-gray-600 mt-0.5">{apt.time}</p>}
                      {apt.technician && <p className="text-sm text-gray-500">Tech: {crmBackend.getTechnicianLabel(apt.technician)}</p>}
                      {apt.location && (
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {apt.location.split('\n')[0]}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-medium">{crmBackend.formatCurrency(apt.amount)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* All upcoming appointments */}
      <Card>
        <CardHeader>
          <CardTitle>All Scheduled Appointments</CardTitle>
          <CardDescription>{allAppointments.length} total</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {allAppointments.map((apt) => (
              <div key={apt.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => openEdit(apt)}>
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-blue-100 text-blue-700">
                    <CalendarIcon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{apt.title}</p>
                    <p className="text-xs text-gray-500">{crmBackend.getTechnicianLabel(apt.technician) || apt.location?.split('\n')[0] || ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={`text-xs ${statusColor(apt.status)}`}>Appt: {apt.status}</Badge>
                  <Badge className={`text-xs ${crmBackend.getJobStatusColor(apt.jobStatus)}`}>Job: {apt.jobStatus}</Badge>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {apt.date ? new Date(apt.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                    </p>
                    <p className="text-xs text-gray-400">{apt.time}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="text-xs h-7"
                      onClick={(e) => { e.stopPropagation(); syncToGCal(apt); }}>
                      GCal
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs h-7"
                      onClick={(e) => { e.stopPropagation(); sendConfirmation(apt); }}>
                      Email
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit appointment dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editDoc?.name} — {editDoc?.party_name || editDoc?.customer_name}
            </DialogTitle>
          </DialogHeader>
          {editDoc && (
            <div className="space-y-3">
              {editDoc.custom_service_address && (
                <div className="text-sm text-gray-600 bg-gray-50 rounded p-3">
                  <p className="font-medium text-gray-700 mb-1">Service Address</p>
                  <p className="whitespace-pre-line">{editDoc.custom_service_address}</p>
                </div>
              )}
              <div>
                <Label>Appointment Status</Label>
                <select
                  className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                  value={editForm.custom_appointment_status}
                  onChange={(e) => setEditForm(f => ({ ...f, custom_appointment_status: e.target.value }))}
                  disabled={isTech}
                >
                  {crmBackend.APPOINTMENT_STATUSES.map(s => (
                    <option key={s} value={s}>{s || '— Not set —'}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Job Status (technician progress)</Label>
                <p className="mt-1 text-sm">
                  <Badge className={crmBackend.getJobStatusColor(editDoc.custom_job_status)}>
                    {crmBackend.normalizeJobStatus(editDoc.custom_job_status)}
                  </Badge>
                </p>
              </div>
              {!isTech && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={editForm.custom_scheduled_date}
                        onChange={(e) => setEditForm(f => ({ ...f, custom_scheduled_date: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Time Slot</Label>
                      <select
                        className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                        value={editForm.custom_scheduled_time}
                        onChange={(e) => setEditForm(f => ({ ...f, custom_scheduled_time: e.target.value }))}
                      >
                        <option value="">— Not set —</option>
                        {crmBackend.timeWindowOptions(editForm.custom_scheduled_time).map((w) => (
                          <option key={w} value={w}>{w}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <TechnicianMultiSelect
                      value={editForm.custom_technician}
                      onChange={(val) => setEditForm(f => ({ ...f, custom_technician: val }))}
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button variant="outline" size="sm" className="flex-1"
                      onClick={() => { setEditOpen(false); syncToGCal({ id: editDoc.name, title: editDoc.party_name }); }}>
                      Sync to Google Calendar
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1"
                      onClick={() => { setEditOpen(false); sendConfirmation({ id: editDoc.name, title: editDoc.party_name }); }}>
                      Email Confirmation
                    </Button>
                  </div>
                </>
              )}
              {isTech && (
                <p className="text-xs text-gray-400">Scheduling changes must be made by the office.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
